import axios from 'axios';
import { IncomingHttpHeaders, IncomingMessage } from 'http';
import { logger as baseLogger } from '../logger';

const ALLOWED_HEADERS = new Set([
  'x-forwarded-for',
  'host',
  'accept',
  'user-agent',
]);

const sanitizeHeaders = (headers: IncomingHttpHeaders | undefined) => {
  if (!headers || typeof headers !== 'object') {
    return undefined;
  }

  const headerEntries = Object.entries(headers);
  if (headerEntries.length === 0) {
    return undefined;
  }

  const sanitizedHeaders: Record<string, unknown> = {};

  headerEntries.forEach(([key, value]) => {
    if (
      ALLOWED_HEADERS.has(key.toLowerCase()) &&
      typeof value !== 'undefined'
    ) {
      sanitizedHeaders[key] = value;
    }
  });

  return Object.keys(sanitizedHeaders).length > 0
    ? sanitizedHeaders
    : undefined;
};

const buildHttpRequestLog = (req: IncomingMessage) => ({
  method: req.method,
  url: req.url,
  headers: sanitizeHeaders(req.headers),
});

const serializeError = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const { name, message, stack, code, config, response } = error;
    return {
      type: 'AxiosError',
      name,
      message,
      stack,
      code,
      request: config
        ? {
            method: config.method,
            url: config.url,
          }
        : undefined,
      response: response
        ? {
            status: response.status,
          }
        : undefined,
    };
  }

  if (error instanceof Error) {
    const { name, message, stack } = error;
    return {
      type: 'Error',
      name,
      message,
      stack,
    };
  }

  return {
    type: typeof error,
    message: String(error),
  };
};

export class ServerSidePropsLogger {
  private logger: typeof baseLogger;
  private requestId: string;

  constructor(requestId: string) {
    this.logger = baseLogger.child({ scope: 'getServerSideProps' });
    this.requestId = requestId;
  }

  public logAccess(req: IncomingMessage) {
    this.logger.info({
      requestId: this.requestId,
      request: buildHttpRequestLog(req),
    });
  }

  public logError(
    error: unknown,
    message = 'SSR request failed',
    level: 'error' | 'warn' = 'error',
  ) {
    const logPayload = {
      requestId: this.requestId,
      error: serializeError(error),
    };

    if (level === 'warn') {
      this.logger.warn(logPayload, message);
    } else {
      this.logger.error(logPayload, message);
    }
  }
}
